import React, { useState } from 'react'
import { Upload, Download, AlertCircle, CheckCircle2, ShieldCheck, Send } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Badge, Checkbox,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Alert, AlertTitle, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../ui'
import { useAuth } from '../../hooks/useAuth'
import { getActiveOrgOverride, DEFAULT_ORG_ID } from '../../lib/supabaseClient'
import {
  createPlayer, getExistingPlayerKeys, getRemainingCapacity,
  orgExigeConsentimiento, solicitarConsentimientoParaJugadores, crearFamiliaConResponsable,
} from '../../lib/supabase'
import { buscarPersonaPorTelefono } from '../../lib/services/personas'
import { agruparPorResponsable, ligarConExistentes, etiquetaFamiliaImportada } from '../../lib/importFamilias'
import { parsePlayersCsv, classifyRows, buildPlayersTemplateCsv, estatusDeImportacion } from '../../lib/csvPlayers'
import { isValidCurp } from '../../lib/curp'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'

const ESTATUS_CON_CURP = ['Activo', 'Pendiente', 'Inactivo', 'Suspendido']

const ImportPlayersModal = ({ open, onOpenChange, onImported }) => {
  const { staff } = useAuth()
  const orgId = getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID

  const [analysis, setAnalysis] = useState(null)
  // Que familia le toca a cada fila: { grupos, sinResponsable, porLinea }. Se calcula
  // al leer el archivo, para que la vista previa lo diga antes de importar.
  const [planFamilias, setPlanFamilias] = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [capacity, setCapacity] = useState(null)
  const [estatusCurp, setEstatusCurp] = useState('Activo')
  const [parseError, setParseError] = useState(null)
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [report, setReport] = useState(null)
  // Si el club exige consentimiento, NINGUN jugador puede nacer Activo: el
  // consentimiento se pide SOBRE un jugador, asi que uno recien creado nunca lo tiene.
  // La pantalla tiene que decirlo antes, no dejar que el usuario elija un estatus que
  // la puerta va a ignorar.
  const [exigeConsentimiento, setExigeConsentimiento] = useState(false)
  const [idsImportados, setIdsImportados] = useState([])
  const [enviandoConsent, setEnviandoConsent] = useState(false)
  const [resultadoConsent, setResultadoConsent] = useState(null)

  const reset = () => {
    setAnalysis(null); setPlanFamilias(null); setSelected(new Set()); setCapacity(null)
    setEstatusCurp('Activo'); setParseError(null); setImporting(false)
    setProgress({ done: 0, total: 0 }); setReport(null)
    setIdsImportados([]); setEnviandoConsent(false); setResultadoConsent(null)
  }

  const guard = useUnsavedGuard({
    onClose: () => {
      // Recargar la lista al CERRAR (no durante el import): si se recarga antes,
      // PlayersManager muestra su spinner y desmonta este modal, perdiendo el reporte.
      const didImport = (report?.importados || 0) > 0
      reset()
      onOpenChange(false)
      if (didImport) onImported?.()
    }
  })

  const handleClose = (v) => {
    if (importing) return
    if (!v) {
      guard.onOpenChange(false)
    } else {
      onOpenChange(v)
    }
  }

  const downloadTemplate = () => {
    const blob = new Blob([buildPlayersTemplateCsv()], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla_jugadores.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setParseError(null); setReport(null)
    const text = await file.text()
    const { rows, errors } = parsePlayersCsv(text)
    if (errors.length) { setParseError(errors[0].message); setAnalysis(null); return }

    const [{ data: existing }, cap, { data: exige }] = await Promise.all([
      getExistingPlayerKeys(),
      getRemainingCapacity(orgId, 'players'),
      orgExigeConsentimiento(orgId),
    ])
    setExigeConsentimiento(Boolean(exige))
    const result = classifyRows(rows, existing)
    guard.markDirty()
    setAnalysis(result)
    setCapacity(cap)
    setSelected(new Set(result.nuevos.map((n) => n.__line)))

    // LAS FAMILIAS SE DECIDEN AQUI, no al importar: el admin ve en la vista previa
    // quien responde por cada fila, que filas son hermanos y que responsable ya
    // existe en el club, y si algo no cuadra corrige el archivo y lo vuelve a cargar.
    // Una busqueda por responsable distinto (por telefono, indice exacto), no por fila.
    const { grupos, sinResponsable } = agruparPorResponsable(result.nuevos)
    const ligados = await Promise.all(grupos.map(async (g) => {
      if (g.telefono.length < 10) return ligarConExistentes(g, [])
      const { data: candidatos } = await buscarPersonaPorTelefono(g.telefono, orgId)
      return ligarConExistentes(g, candidatos || [])
    }))
    const porLinea = new Map()
    ligados.forEach((g) => g.filas.forEach((f) => porLinea.set(f.__line, g)))
    setPlanFamilias({ grupos: ligados, sinResponsable, porLinea })
  }

  const toggleRow = (line) => {
    guard.markDirty()
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(line)) next.delete(line); else next.add(line)
      return next
    })
  }

  // El estatus REAL con el que va a quedar la fila. La vista previa y la importacion
  // usan el mismo calculo: antes la tabla prometia "Activo" y la puerta lo degradaba a
  // Pendiente sin decir nada.
  const estatusReal = (n) => estatusDeImportacion({
    exigeConsentimiento,
    curpValida: isValidCurp(n.curp),
    estatusElegido: estatusCurp,
  })

  const selectedNuevos = analysis ? analysis.nuevos.filter((n) => selected.has(n.__line)) : []
  const selectedCount = selectedNuevos.length
  const overCapacity = capacity && !capacity.unlimited && !capacity.readOnly && selectedCount > capacity.remaining
  const canImport = analysis && !importing && selectedCount > 0 && !capacity?.readOnly && !overCapacity

  const handleImport = async () => {
    setImporting(true)
    setProgress({ done: 0, total: selectedCount })
    let importados = 0
    let errores = 0
    let pendientesPorConsentimiento = 0
    const ids = []
    const idPorLinea = new Map()
    for (const n of selectedNuevos) {
      const payload = { ...n, estatus: estatusReal(n) }
      delete payload.__line
      const { data, error, aviso } = await createPlayer(payload)
      if (error) {
        errores += 1
      } else {
        importados += 1
        if (data?.id) { ids.push(data.id); idPorLinea.set(n.__line, data.id) }
        if (aviso) pendientesPorConsentimiento += 1
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }))
    }
    setIdsImportados(ids)

    // Las familias, DESPUES de las fichas y por grupo: persona -> familia -> vinculo ->
    // jugadores, el mismo camino del alta. Si una familia falla, sus jugadores ya
    // quedaron guardados y salen en Fichas por completar; no se pierde ninguno.
    let familiasNuevas = 0
    let sumadosAExistentes = 0
    let sinFamiliaPorError = 0
    for (const g of (planFamilias?.grupos || [])) {
      const jugadorIds = g.filas.map((f) => idPorLinea.get(f.__line)).filter(Boolean)
      if (jugadorIds.length === 0) continue
      const { data, error } = await crearFamiliaConResponsable({
        responsable: { nombre: g.nombre, telefono: g.telefono, email: g.email, esElJugador: g.esElJugador },
        jugadorIds,
        nombreFallback: g.filas[0]?.nombre_completo || '',
        personaId: g.existente?.personaId || null,
        familiaId: g.existente?.familiaId || null,
      })
      if (error) { sinFamiliaPorError += jugadorIds.length; continue }
      if (data?.creada) familiasNuevas += 1; else sumadosAExistentes += data?.asignados || 0
    }
    const sinResponsable = (planFamilias?.sinResponsable || [])
      .filter((f) => idPorLinea.has(f.__line)).length + sinFamiliaPorError

    setReport({
      importados,
      pendientesPorConsentimiento,
      omitidos: analysis.duplicados.length,
      errores: errores + analysis.errores.length,
      familias: { familiasNuevas, sumadosAExistentes, sinResponsable },
    })
    guard.resetDirty()
    setImporting(false)
    // La recarga de la lista se hace al cerrar el modal (handleClose), para que
    // el reporte no se pierda por el re-render/spinner de PlayersManager.
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent {...guard.dirtyProps} className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Importar jugadores</DialogTitle>
        </DialogHeader>

        {report ? (
          <div className="space-y-3">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Importación terminada</AlertTitle>
              <AlertDescription>
                Importados: {report.importados} · Omitidos (ya existen): {report.omitidos} · Errores: {report.errores}
                {report.familias && (
                  <span className="block mt-1">
                    Familias nuevas: {report.familias.familiasNuevas} · Sumados a familias que ya existían: {report.familias.sumadosAExistentes}
                    {report.familias.sinResponsable > 0 && (
                      <> · Sin responsable: {report.familias.sinResponsable} (quedan en Fichas por completar)</>
                    )}
                  </span>
                )}
              </AlertDescription>
            </Alert>

            {report.pendientesPorConsentimiento > 0 && (
              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertTitle>
                  {report.pendientesPorConsentimiento === 1
                    ? '1 jugador quedó Pendiente'
                    : `${report.pendientesPorConsentimiento} jugadores quedaron Pendientes`}
                </AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    Este club exige el consentimiento del tutor para activar. Se activan solos
                    en cuanto el tutor firme.
                  </p>
                  {resultadoConsent ? (
                    <p className="font-medium text-foreground">
                      {resultadoConsent.solicitudes === 0
                        ? 'No hubo solicitudes por enviar: puede que ya se hubieran pedido antes.'
                        : `Listo. ${resultadoConsent.solicitudes} ${resultadoConsent.solicitudes === 1 ? 'solicitud enviada' : 'solicitudes enviadas'} para ${resultadoConsent.jugadores} jugador(es).`}
                    </p>
                  ) : (
                    <Button
                      size="sm"
                      disabled={enviandoConsent || idsImportados.length === 0}
                      onClick={async () => {
                        setEnviandoConsent(true)
                        const { data } = await solicitarConsentimientoParaJugadores(idsImportados, orgId)
                        setResultadoConsent(data || { solicitudes: 0, jugadores: 0 })
                        setEnviandoConsent(false)
                      }}
                    >
                      <Send className="mr-1.5 h-3.5 w-3.5" />
                      {enviandoConsent ? 'Enviando...' : 'Solicitar consentimiento a los tutores'}
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-1" /> Descargar plantilla
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} disabled={importing} />
                <span className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm cursor-pointer hover:bg-accent">
                  <Upload className="h-4 w-4" /> Subir CSV
                </span>
              </label>
            </div>

            {parseError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No pudimos leer el archivo</AlertTitle>
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}

            {capacity?.readOnly && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Suscripcion en solo lectura</AlertTitle>
                <AlertDescription>No puedes importar jugadores con la suscripcion pausada.</AlertDescription>
              </Alert>
            )}

            {analysis && (
              <>
                <div className="flex flex-wrap gap-2 text-sm">
                  <Badge variant="outline">{analysis.nuevos.length} nuevos</Badge>
                  <Badge variant="secondary">{analysis.duplicados.length} ya existen</Badge>
                  <Badge variant="destructive">{analysis.errores.length} con error</Badge>
                </div>

                {capacity && !capacity.readOnly && (
                  <p className={overCapacity ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
                    {capacity.unlimited
                      ? `Plan sin limite. Seleccionados: ${selectedCount}.`
                      : `Tu plan permite ${capacity.limit}. Tienes ${capacity.used}. Puedes agregar hasta ${capacity.remaining}. Seleccionados: ${selectedCount}.`}
                    {overCapacity && ' Deselecciona filas para no exceder tu plan.'}
                  </p>
                )}

                {exigeConsentimiento ? (
                  <Alert>
                    <ShieldCheck className="h-4 w-4" />
                    <AlertTitle>Este club exige consentimiento del tutor</AlertTitle>
                    <AlertDescription>
                      Los jugadores se importan como <strong>Pendiente</strong> y se activan
                      cuando el tutor firme. Al terminar podrás pedirle el consentimiento a
                      todos de una vez.
                    </AlertDescription>
                  </Alert>
                ) : analysis.nuevos.some((n) => isValidCurp(n.curp)) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Estatus para los que traen CURP:</span>
                    <Select value={estatusCurp} onValueChange={(v) => { guard.markDirty(); setEstatusCurp(v) }}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ESTATUS_CON_CURP.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">(sin CURP -&gt; Pendiente)</span>
                  </div>
                )}

                {analysis.nuevos.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>CURP</TableHead>
                        <TableHead>Estatus</TableHead>
                        <TableHead>Responsable / Familia</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analysis.nuevos.map((n) => (
                        <TableRow key={n.__line}>
                          <TableCell>
                            <Checkbox checked={selected.has(n.__line)} onCheckedChange={() => toggleRow(n.__line)} />
                          </TableCell>
                          <TableCell>{n.nombre_completo}</TableCell>
                          <TableCell className="text-xs">{n.curp || '—'}</TableCell>
                          <TableCell className="text-xs">{estatusReal(n)}</TableCell>
                          <TableCell className="text-xs">
                            {(() => {
                              const et = etiquetaFamiliaImportada(planFamilias?.porLinea?.get(n.__line) || null)
                              return (
                                <span className={et.tono === 'aviso' ? 'text-amber-700 dark:text-amber-400' : ''}>
                                  {planFamilias ? et.texto : 'Revisando...'}
                                </span>
                              )
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {analysis.errores.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium text-destructive">Filas con error (no se importan):</p>
                    {analysis.errores.slice(0, 10).map((e, i) => (
                      <p key={i}>Linea {e.row.__line}: {e.reason}</p>
                    ))}
                  </div>
                )}
              </>
            )}

            {importing && (
              <p className="text-sm text-muted-foreground">Importando {progress.done} / {progress.total}...</p>
            )}
          </div>
        )}

        <DialogFooter>
          {report ? (
            <Button onClick={() => handleClose(false)}>Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={importing}>Cancelar</Button>
              <Button onClick={handleImport} disabled={!canImport}>
                Importar {selectedCount > 0 ? `(${selectedCount})` : ''}
              </Button>
            </>
          )}
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default ImportPlayersModal
